import { DayPicker, Matcher } from "react-day-picker";
import "./simple-calendar.scss";
import SimpleCard from "../simple-card";
import { Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FC, useMemo, useState } from "react";
import { getDayTooltip } from "lib/utils/util";
import { DateTime } from "luxon";
import { getArrayFromTo } from "lib/utils/array-utils";
import { CALENDER_COLORS, UNAVAILABLE_DAYS } from "lib/constants";
import { areDatesEqual, sortDates } from "lib/utils/date-utils";

const renderTooltip = (unavailableDays: UnavailableDay[], date: Date) => {
  const text = getDayTooltip(unavailableDays, date);

  return (
    <Tooltip id={`tooltip-${date}`}>
      {text === "" ? "Unavailable" : text}
    </Tooltip>
  );
};

interface SimpleCalendarProps {
  className?: string;
  disabled?: Matcher | Matcher[];
  isDimWeekends?: boolean;
  isNavigationDisabled?: boolean;
  mode?: "single" | "multiple" | "range";
  selectedDate?: Date;
  selectedDates?: Date[];
  selectedWeekdays?: string[];
  showWeekDaySelection?: boolean;
  unavailableDays?: UnavailableDay[];
  isEditable?: boolean;
  disableSundays?: boolean;

  onChange?: (date: Date) => void;
  onSelectedDatesChange?: (dates: Date[]) => void;
  onWeekdayChange?: (selectedWeekdays: string[]) => void;
}

const SimpleCalendar: FC<SimpleCalendarProps> = ({
  className,
  disabled,
  isDimWeekends = true,
  isNavigationDisabled = false,
  mode = "single",
  selectedDate,
  selectedDates,
  selectedWeekdays = [],
  showWeekDaySelection = false,
  unavailableDays = [],
  isEditable = false,
  disableSundays = false,

  onWeekdayChange,
  onChange,
  onSelectedDatesChange,
}) => {
  const [currentMonth, setCurrentMonth] = useState<number>(
    DateTime.now().month
  );

  // const [currentSelectedDates, setCurrentSelectedDates] = useState<Date[]>(
  //   selectedDates ?? []
  // );

  const holidays = useMemo(
    () =>
      (unavailableDays ?? [])
        .filter((day) => day?.type === UNAVAILABLE_DAYS.HOLIDAY)
        .map((day) => DateTime.fromISO(day?.date).toJSDate()),
    [unavailableDays]
  );

  const closedDays = useMemo(
    () =>
      (unavailableDays ?? [])
        .filter((day) => day?.type === UNAVAILABLE_DAYS.UNAVAILABLE)
        .map((day) => DateTime.fromISO(day?.date).toJSDate()),
    [unavailableDays]
  );

  const weekends = useMemo(() => {
    if (isDimWeekends) {
      const allDays = getArrayFromTo(1, 31);

      const weekends = allDays.filter((day) => {
        const date = DateTime.now().set({ day, month: currentMonth });
        if(date.weekday === 7 && disableSundays){
          return false;
        }else{
          return date.weekday === 6 || date.weekday === 7;
        }
      });

      return weekends.map((day) =>
        DateTime.now().set({ day, month: currentMonth }).toJSDate()
      );
    } else {
      return [];
    }
  }, [currentMonth]);

  const handleWeekdayChange = (weekday: string) => {
    let updatedWeekdays: string[];

    if (selectedWeekdays.includes(weekday)) {
      updatedWeekdays = selectedWeekdays.filter((day) => day !== weekday);
    } else {
      updatedWeekdays = [...selectedWeekdays, weekday];
    }

    onWeekdayChange?.(updatedWeekdays);
  };

  const handleDateSelection = (date: Date) => {
    if (
      !isEditable &&
      (unavailableDays ?? []).some((day) =>
        areDatesEqual(new Date(day?.internalDate), date)
      )
    ) {
      return; // Do not allow selection of unavailable dates
    }

    onChange?.(date);
    if (mode === "single") {
      onSelectedDatesChange?.([date]);
    } else if (mode === "multiple") {
      if ((selectedDates ?? []).some((d) => areDatesEqual(d, date))) {
        const filteredDates = sortDates(
          (selectedDates ?? []).filter((d) => !areDatesEqual(d, date))
        );

        onSelectedDatesChange?.(filteredDates);
      } else {
        const sortedDates = sortDates([...(selectedDates ?? []), date]);
        onSelectedDatesChange?.(sortedDates);
      }
    }
  };

  return (
    <SimpleCard className={`simple-calendar ${className}`}>
      <DayPicker
        className="w-100"
        animate
        mode="single"
        weekStartsOn={1}
        disabled={
          disableSundays
            ? [
                // include any existing disabled matchers if provided
                ...(Array.isArray(disabled) ? disabled : disabled ? [disabled] : []),
                // ensure we pass number 0 (Sunday) — DayPicker uses 0 = Sunday
                { dayOfWeek: [0] },
              ]
            : Array.isArray(disabled)
            ? disabled
            : disabled
            ? [disabled]
            : []
        }
        selected={selectedDate}
        disableNavigation={isNavigationDisabled}
        modifiers={{
          holidays: holidays,
          closedDays: closedDays,
          weekends: weekends,
          selectedDates: selectedDates,
        }}
        modifiersStyles={{
          holidays: {
            backgroundColor: CALENDER_COLORS.HOLIDAY,
            borderRadius: "50%",
          },
          closedDays: {
            backgroundColor: CALENDER_COLORS.UNAVAILABLE,
            borderRadius: "50%",
          },
          weekends: {
            color: "#ccc",
          },
          selectedDates: {
            backgroundColor: CALENDER_COLORS.SELECTED,
            borderRadius: "50%",
          },
        }}
        onMonthChange={(month) => setCurrentMonth(month.getMonth() + 1)}
        components={{
          DayButton: ({ day, modifiers, ...props }) => {
            const isHoliday = modifiers.holidays;
            const isBooked = modifiers.bookedDays;

            if (isBooked || isHoliday) {
              return (
                <OverlayTrigger
                  placement="top"
                  delay={{ show: 250, hide: 400 }}
                  overlay={renderTooltip(unavailableDays, day.date)}
                >
                  <button {...props}>{day.date.getDate()}</button>
                </OverlayTrigger>
              );
            } else {
              return <button {...props}>{day.date.getDate()}</button>;
            }
          },
          Weekday: (props) => {
            if (showWeekDaySelection) {
              return (
                <th {...props}>
                  <div
                    className="d-flex align-items-center gap-1 123"
                    //onClick={() => handleWeekdayChange(props["aria-label"])}
                  >
                    <Form.Check
                      type="checkbox"
                      checked={selectedWeekdays.includes(props["aria-label"])}
                      onChange={() => handleWeekdayChange(props["aria-label"])}
                      disabled={disableSundays && props["aria-label"] === "Sunday"}
                    />
                    {props.children}
                  </div>
                </th>
              );
            } else {
              return <th {...props}>{props.children}</th>;
            }
          },
        }}
        onDayClick={handleDateSelection}
        onSelect={handleDateSelection}
      />
    </SimpleCard>
  );
};

export default SimpleCalendar;
